import { RESERVED_FUNCTION_KEYS } from "src/modules/data-protection/constants/organizationFunctions";

export const REQUIRED_APPROVALS_VIEW_MODES = {
    empty: "empty",
    list: "list",
};

export const APPROVAL_REQUIREMENTS_QUERY_KEY = "gdpr-software-approval-requirements";

export const invalidateApprovalRelatedQueries = (queryClient, softwareId) => {
    if (!queryClient || !softwareId) {
        return;
    }

    queryClient.invalidateQueries([
        APPROVAL_REQUIREMENTS_QUERY_KEY,
        softwareId,
    ]);
    queryClient.invalidateQueries([
        "data-protection-software-details",
        softwareId,
    ]);
    queryClient.invalidateQueries([
        "gdpr-software-basic-information",
        softwareId,
    ]);
    queryClient.invalidateQueries([
        "gdpr-software-compliance-overview",
        softwareId,
    ]);
    queryClient.invalidateQueries(["data-protection-fetch-overview-content"]);
};

export const APPROVAL_REQUIREMENT_TYPES = {
    ASSET_OWNER: "asset_owner",
    DPO: "dpo",
    AI_GOVERNANCE: "ai_governance",
    IT_SECURITY: "it_security",
};

/**
 * Backend type `asset_owner` is the Assigned Analyst card. Never show
 * "Asset Owner" in the UI.
 */
export const APPROVAL_REQUIREMENT_TYPE_LABELS = {
    [APPROVAL_REQUIREMENT_TYPES.ASSET_OWNER]: "Assigned Analyst",
};

export const APPROVAL_REQUIREMENT_STATUSES = {
    MISSING_ASSIGNEE: "missing_assignee",
    PENDING: "pending",
    APPROVED: "approved",
    REJECTED: "rejected",
};

const EMPTY_PAYLOAD = {
    initialized: false,
    status: "not_started",
    requirements: [],
};

const hasPresentValue = (value) => value != null && value !== "";

const toBoolean = (value) =>
    value === true || value === 1 || value === "1" || value === "true";

export const getRequirementTitle = (requirement = {}) => {
    const typeLabel = APPROVAL_REQUIREMENT_TYPE_LABELS[requirement.type];

    if (typeLabel) {
        return typeLabel;
    }

    if (hasPresentValue(requirement.label)) {
        return String(requirement.label);
    }

    if (hasPresentValue(requirement.title)) {
        return String(requirement.title);
    }

    return "";
};

export const unwrapEligibleAssignees = (payload) => {
    let current = payload;

    if (
        current?.data &&
        !Array.isArray(current) &&
        !Array.isArray(current?.eligible_assignees) &&
        !Array.isArray(current?.users)
    ) {
        current = current.data;
    }

    const list = Array.isArray(current)
        ? current
        : current?.eligible_assignees ||
          current?.eligibleAssignees ||
          current?.users ||
          current?.items ||
          current?.assignees ||
          [];

    return list
        .map((user) => {
            const id = user?.id ?? user?.user_id ?? user?.value;
            const name = getPersonDisplayName(user);

            if (id == null || id === "") {
                return null;
            }

            return {
                id,
                name: name || String(id),
            };
        })
        .filter(Boolean);
};

const normalizeText = (value) => {
    if (!hasPresentValue(value)) {
        return "";
    }

    return String(value).trim().toLowerCase();
};

const looksLikeRequirementsPayload = (value) =>
    Boolean(
        value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            ("initialized" in value || Array.isArray(value.requirements))
    );

export const unwrapApprovalRequirementsPayload = (payload) => {
    let current = payload;
    const seen = new Set();

    while (
        current &&
        typeof current === "object" &&
        !Array.isArray(current) &&
        !seen.has(current)
    ) {
        seen.add(current);

        if (looksLikeRequirementsPayload(current)) {
            return {
                initialized: current.initialized,
                status: current.status,
                requirements: Array.isArray(current.requirements)
                    ? current.requirements
                    : [],
            };
        }

        current = current.data;
    }

    return { ...EMPTY_PAYLOAD, requirements: [] };
};

export const isApprovalRequirementsEmpty = (payload) => {
    const data = looksLikeRequirementsPayload(payload)
        ? {
              initialized: payload.initialized,
              requirements: Array.isArray(payload.requirements)
                  ? payload.requirements
                  : [],
          }
        : unwrapApprovalRequirementsPayload(payload);
    const requirements = Array.isArray(data.requirements)
        ? data.requirements
        : [];

    return (
        data.initialized === false ||
        (!data.initialized && !requirements.length)
    );
};

export const parseApprovalRequirementsPayload = (payload) => {
    const unwrapped = unwrapApprovalRequirementsPayload(payload);
    const requirements = Array.isArray(unwrapped.requirements)
        ? unwrapped.requirements
        : [];

    return {
        initialized: unwrapped.initialized === true,
        status: unwrapped.status || "not_started",
        requirements,
        isEmpty: isApprovalRequirementsEmpty(unwrapped),
    };
};

export const getRequiredApprovalsViewMode = (payload) => {
    if (isApprovalRequirementsEmpty(payload)) {
        return REQUIRED_APPROVALS_VIEW_MODES.empty;
    }

    return REQUIRED_APPROVALS_VIEW_MODES.list;
};

export const isReservedFunctionKey = (key) =>
    Object.values(RESERVED_FUNCTION_KEYS).includes(key);

export const isAssetOwnerRequirement = (requirement) =>
    requirement?.type === APPROVAL_REQUIREMENT_TYPES.ASSET_OWNER;

export const isFunctionRequirement = (requirement) => {
    if (isAssetOwnerRequirement(requirement)) {
        return false;
    }

    return isReservedFunctionKey(requirement?.source_function_key);
};

export const getPersonDisplayName = (value) => {
    if (value == null || value === "") {
        return null;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        if (hasPresentValue(value.full_name)) {
            return String(value.full_name);
        }

        if (hasPresentValue(value.name)) {
            return String(value.name);
        }

        const combined = [value.first_name, value.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();

        return combined || null;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();

        if (!trimmed || /^\d+$/.test(trimmed)) {
            return null;
        }

        return trimmed;
    }

    return null;
};

export const getAssigneeRoleLabel = (user) => {
    if (!user || typeof user !== "object") {
        return null;
    }

    const employeeInfo =
        user.employeeInfo && typeof user.employeeInfo === "object"
            ? user.employeeInfo
            : {};

    const candidates = [
        user.position,
        user.job_title,
        user.jobTitle,
        employeeInfo.position,
        employeeInfo.job_title,
        typeof user.role === "string" ? user.role : null,
    ];

    const match = candidates.find(
        (item) => typeof item === "string" && item.trim()
    );

    return match ? match.trim() : null;
};

export const getRequirementStatusPresentation = (status) => {
    const normalized = normalizeText(status);

    if (normalized === APPROVAL_REQUIREMENT_STATUSES.MISSING_ASSIGNEE) {
        return {
            variant: "badge",
            color: "secondary",
            labelKey: "Missing Assignee",
            showDot: false,
        };
    }

    if (normalized === APPROVAL_REQUIREMENT_STATUSES.APPROVED) {
        return {
            variant: "badge",
            color: "success",
            labelKey: "Approved",
            showDot: true,
        };
    }

    if (normalized === APPROVAL_REQUIREMENT_STATUSES.REJECTED) {
        return {
            variant: "badge",
            color: "danger",
            labelKey: "Rejected",
            showDot: true,
        };
    }

    if (normalized === APPROVAL_REQUIREMENT_STATUSES.PENDING) {
        return {
            variant: "badge",
            color: "warning",
            labelKey: "Awaiting approval",
            showDot: true,
        };
    }

    return {
        variant: "badge",
        color: "secondary",
        labelKey: hasPresentValue(status) ? String(status) : "Not assigned",
        showDot: false,
    };
};

const getAssigneeId = (assignee) => {
    if (assignee == null || assignee === "") {
        return null;
    }

    if (typeof assignee === "object" && !Array.isArray(assignee)) {
        return assignee.id != null && assignee.id !== "" ? assignee.id : null;
    }

    return assignee;
};

export const mapRequirementToViewModel = (requirement = {}) => {
    const status = normalizeText(requirement.status);
    const isAssetOwner = isAssetOwnerRequirement(requirement);
    const isFunction = isFunctionRequirement(requirement);
    const isMissingAssignee =
        status === APPROVAL_REQUIREMENT_STATUSES.MISSING_ASSIGNEE;
    const canAssign = toBoolean(requirement.can_assign);
    const canDecide = toBoolean(requirement.can_decide);
    const assigneeId = getAssigneeId(requirement.assignee);
    const hasAssignee = assigneeId != null;
    const assignmentDate = hasPresentValue(requirement.assigned_at)
        ? requirement.assigned_at
        : hasPresentValue(requirement.updated_at)
        ? requirement.updated_at
        : null;

    return {
        id: requirement.id,
        type: requirement.type || "",
        title: getRequirementTitle(requirement),
        sourceFunctionKey: requirement.source_function_key ?? null,
        status,
        canAssign,
        canDecide,
        assigneeId,
        isAssetOwner,
        isFunction,
        isMissingAssignee,
        showAssign: canAssign && !hasAssignee,
        showChangeAssignee: canAssign && hasAssignee,
        showApproveReject:
            canDecide && status === APPROVAL_REQUIREMENT_STATUSES.PENDING,
        showChangeDecision:
            canDecide &&
            (status === APPROVAL_REQUIREMENT_STATUSES.APPROVED ||
                status === APPROVAL_REQUIREMENT_STATUSES.REJECTED),
        showMissingAssigneeHelper: false,
        showGoToAssessment: !isAssetOwner,
        statusPresentation: getRequirementStatusPresentation(status),
        assignedToName: getPersonDisplayName(requirement.assignee),
        assignedToRole: getAssigneeRoleLabel(requirement.assignee),
        assignedByName: getPersonDisplayName(requirement.assigned_by),
        assignedByIsNull: requirement.assigned_by == null,
        assignmentSource: requirement.assignment_source || null,
        lastUpdated: assignmentDate,
        approvalDate:
            status === APPROVAL_REQUIREMENT_STATUSES.APPROVED &&
            hasPresentValue(requirement.approved_at)
                ? requirement.approved_at
                : null,
        rejectionDate:
            status === APPROVAL_REQUIREMENT_STATUSES.REJECTED &&
            hasPresentValue(requirement.rejected_at)
                ? requirement.rejected_at
                : null,
        rejectionReason:
            status === APPROVAL_REQUIREMENT_STATUSES.REJECTED &&
            hasPresentValue(requirement.rejection_reason)
                ? String(requirement.rejection_reason)
                : null,
    };
};

export const mapApprovalRequirements = (payload) => {
    const parsed = parseApprovalRequirementsPayload(payload);

    if (parsed.isEmpty) {
        return [];
    }

    return parsed.requirements.map(mapRequirementToViewModel);
};

export const canSubmitApprovalAction = ({ canDecide, status } = {}) => {
    return (
        toBoolean(canDecide) &&
        normalizeText(status) === APPROVAL_REQUIREMENT_STATUSES.PENDING
    );
};

export const isRejectionReasonValid = (reason) =>
    String(reason || "").trim() !== "";

export const buildApprovalRequirementActionPayload = ({
    action,
    rejectionReason,
} = {}) => {
    if (action === "approve") {
        return { action: "approve" };
    }

    if (action === "reject") {
        return {
            action: "reject",
            rejection_reason: String(rejectionReason || "").trim(),
        };
    }

    return null;
};

const extractErrorCode = (error) => {
    const data = error?.response?.data;

    if (!data) {
        return null;
    }

    if (typeof data.error === "string") {
        return data.error;
    }

    if (typeof data.message === "string") {
        return data.message;
    }

    if (typeof data.message?.error === "string") {
        return data.message.error;
    }

    return null;
};

export const getApprovalRequirementActionErrorKey = (error) => {
    const code = extractErrorCode(error);

    if (code === "error.software_approval_not_assignee") {
        return "You are not the assigned approver.";
    }

    if (code === "not_eligible") {
        return "Selected user is not eligible for this approval.";
    }

    if (code === "error.software_approval_case_closed_for_decision") {
        return "This approval can no longer be changed because the case is closed.";
    }

    if (code === "error.responsible_person_required") {
        return "A Responsible Person must be assigned before this software can be approved. Please update the Basic Information section and try again.";
    }

    if (
        code === "error.rejection_reason_required" ||
        code === "rejection_reason_required" ||
        code === "This field cannot be blank"
    ) {
        return "Please provide a reason for rejection.";
    }

    return "The approval could not be completed. Please try again.";
};

export const getMissingFunctionModalCopy = (key) => {
    if (key === RESERVED_FUNCTION_KEYS.DPO) {
        return {
            titleKey: "No DPO assigned",
            bodyKey:
                "This approval requires a DPO, but no user currently has this role assigned.",
            actionKey:
                "Assign the DPO role to a user in User Management before continuing. Once a user has this role, they'll appear as an eligible approver here.",
            alertColor: "warning",
            interpolateTitle: false,
        };
    }

    if (key === RESERVED_FUNCTION_KEYS.IT_SECURITY) {
        return {
            titleKey: "IT Security role not configured",
            bodyKey:
                "This approval requires an IT Security approver, but the required role has not been configured yet.",
            actionKey:
                "Create and configure the IT Security role in User Management. Define the permissions and then assign it to a user before continuing.",
            alertColor: "danger",
            interpolateTitle: false,
        };
    }

    return {
        titleKey: "No eligible user for {{title}}",
        bodyKey:
            "This approval requires a {{title}} approver, but no user currently has this role assigned.",
        actionKey:
            "Assign the {{title}} role to a user in User Management before continuing. Once a user has this role, they'll appear as an eligible approver here.",
        alertColor: "warning",
        interpolateTitle: true,
    };
};
